"use client";

import {
  Badge,
  Box,
  Button,
  HStack,
  Menu,
  Portal,
  Text,
} from "@chakra-ui/react";
import type { MindstringPrototype } from "@/generated/fastapi";
import { getPrototypeTone } from "@/shared/config/prototypeTheme";
import GenericIcon from "../actions/GenericIcon";
import type { GenericIconType } from "../actions/GenericIcon";
import type { GenericOptionItem } from "./option.types";

type MenuSelectDetails = {
  value: string;
};

export type BrandedPrototypeSelectorOption =
  GenericOptionItem<MindstringPrototype> & {
  icon: GenericIconType;
  count?: number;
};

type BrandedPrototypeSelectorVariant = "studio" | "compact";

export type BrandedPrototypeSelectorProps = {
  activePrototype: MindstringPrototype;
  options: BrandedPrototypeSelectorOption[];
  onChange: (value: MindstringPrototype) => void;
  ariaLabel?: string;
  disabled?: boolean;
  showCounts?: boolean;
  showDescriptions?: boolean;
  triggerDescription?: boolean;
  variant?: BrandedPrototypeSelectorVariant;
  countNoun?: string;
  countNounPlural?: string;
};

type PrototypeIconFrameProps = {
  active?: boolean;
  activeBg?: string;
  icon: GenericIconType;
  label: string;
  prototype: MindstringPrototype;
  size: "sm" | "md";
};

function PrototypeIconFrame({
  active = false,
  activeBg,
  icon,
  label,
  prototype,
  size,
}: PrototypeIconFrameProps) {
  const tone = getPrototypeTone(prototype);
  const frameSize = size === "md" ? "10" : "8";
  const iconSize = size === "md" ? undefined : "sm";

  return (
    <Box
      flexShrink={0}
      w={frameSize}
      h={frameSize}
      borderRadius="md"
      bg={active ? (activeBg ?? tone.accentBg) : "moveon.surface.elevatedBg"}
      color={active ? tone.accent : "moveon.text.muted"}
      display="grid"
      placeItems="center"
    >
      <GenericIcon icon={icon} humanReadable={label} size={iconSize} />
    </Box>
  );
}

export default function BrandedPrototypeSelector({
  activePrototype,
  ariaLabel = "Choose perspective",
  countNoun = "situation",
  countNounPlural = `${countNoun}s`,
  disabled = false,
  onChange,
  options,
  showCounts = false,
  showDescriptions = true,
  triggerDescription = false,
  variant = "compact",
}: BrandedPrototypeSelectorProps) {
  const activeOption = options.find(
    (option) => option.value === activePrototype,
  );
  const activeTone = getPrototypeTone(activePrototype);
  const isStudioVariant = variant === "studio";
  const formatCountLabel = (count: number) =>
    `${count} ${count === 1 ? countNoun : countNounPlural}`;

  return (
    <Box
      display="flex"
      h={isStudioVariant ? "full" : undefined}
      alignSelf="stretch"
    >
      <Menu.Root
        onSelect={(details: MenuSelectDetails) => {
          onChange(details.value as MindstringPrototype);
        }}
      >
        <Menu.Trigger asChild>
          <Button
            variant="ghost"
            h={isStudioVariant ? "full" : "auto"}
            minH={isStudioVariant ? { base: "12", md: "14" } : "11"}
            minW="0"
            w={isStudioVariant ? undefined : "full"}
            maxW="full"
            px={isStudioVariant ? "1" : "2"}
            py="1"
            borderRadius="md"
            borderWidth={isStudioVariant ? undefined : "1"}
            borderColor={isStudioVariant ? undefined : activeTone.accentBorder}
            bg={isStudioVariant ? undefined : activeTone.accentBg}
            justifyContent="flex-start"
            whiteSpace="normal"
            disabled={disabled}
            aria-label={ariaLabel}
          >
            <HStack minW={0} w="full" gap="3" align="center">
              <PrototypeIconFrame
                active={true}
                icon={activeOption?.icon ?? "job_activity"}
                label={activeOption?.label ?? "Perspective"}
                prototype={activePrototype}
                size="md"
              />
              <Box minW={0} flex="1" textAlign="start">
                <HStack gap="2" align="center" flexWrap="wrap">
                  <Text
                    as="span"
                    fontSize={isStudioVariant ? { base: "md", md: "lg" } : "md"}
                    fontWeight="semibold"
                    color="moveon.surface.fg"
                    lineHeight="short"
                  >
                    {activeOption?.label ?? "Perspective"}
                  </Text>
                  {showCounts && activeOption?.count !== undefined ? (
                    <Badge
                      variant="surface"
                      colorPalette="gray"
                      fontWeight="medium"
                    >
                      {formatCountLabel(activeOption.count)}
                    </Badge>
                  ) : null}
                  <Box as="span" color={activeTone.accent}>
                    <GenericIcon
                      icon="expand_more"
                      humanReadable="Open perspective menu"
                      size="xs"
                    />
                  </Box>
                </HStack>
                {triggerDescription && activeOption?.description ? (
                  <Box display={{ base: "none", md: "block" }}>
                    <Text
                      as="span"
                      display="block"
                      mt="1"
                      fontSize="sm"
                      fontWeight="normal"
                      color="moveon.text.muted"
                      lineClamp={1}
                    >
                      {activeOption.description}
                    </Text>
                  </Box>
                ) : null}
              </Box>
            </HStack>
          </Button>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content
              bg="moveon.surface.bg"
              color="moveon.surface.fg"
              borderColor="moveon.surface.border"
              minW={{ base: "xs", md: "sm" }}
              maxW="calc(100vw - 2rem)"
              zIndex="max"
            >
              {options.map((option) => {
                const isActive = option.value === activePrototype;
                const optionTone = getPrototypeTone(option.value);

                return (
                  <Menu.Item
                    key={option.value}
                    value={option.value}
                    bg={isActive ? optionTone.accentBg : undefined}
                    fontWeight={isActive ? "semibold" : undefined}
                  >
                    <HStack w="full" align="flex-start" gap="3">
                      <PrototypeIconFrame
                        active={isActive}
                        icon={option.icon}
                        label={option.label}
                        prototype={option.value}
                        size="sm"
                        activeBg={
                          isStudioVariant ? "moveon.surface.bg" : undefined
                        }
                      />
                      <Box minW={0} flex="1">
                        <HStack gap="2" justify="space-between" align="center">
                          <Text as="span" fontWeight="semibold">
                            {option.label}
                          </Text>
                          {showCounts && option.count !== undefined ? (
                            <Badge
                              variant="surface"
                              colorPalette={isActive ? undefined : "gray"}
                              bg={isActive ? "moveon.surface.bg" : undefined}
                              borderColor={
                                isActive ? optionTone.accentBorder : undefined
                              }
                              color={isActive ? optionTone.accent : undefined}
                            >
                              {formatCountLabel(option.count)}
                            </Badge>
                          ) : null}
                        </HStack>
                        {showDescriptions && option.description ? (
                          <Text
                            as="span"
                            display="block"
                            mt="0.5"
                            fontSize="xs"
                            color="moveon.text.muted"
                            lineClamp={2}
                          >
                            {option.description}
                          </Text>
                        ) : null}
                      </Box>
                    </HStack>
                  </Menu.Item>
                );
              })}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </Box>
  );
}
